import { useMemo, useState } from 'react';
import { SOCIAL_STATUS_LABELS, SOCIAL_STATUS_TONES } from './queries';
import type { SocialPost } from './queries';
import { platformStyles } from './platforms';
import { formatKind, pillTime } from './linkedinCalendar';
import { FormatDot } from './FormatDot';
import './linkedinCalendar.css';
import type { CSSProperties } from 'react';

/**
 * The month grid.
 *
 * Angel's whole job here is cadence — seeing that next week has four posts and
 * the week after has none. A list cannot show a gap; only a grid can, because
 * the gap is an empty cell rather than an absent row.
 *
 * Every one of the 105 migrated posts carries a publish date, so an
 * "unscheduled" tray would be permanently empty and is not built. Undated
 * posts are reachable from the Board and the table.
 */

/*
 * Every post on its day, however many there are.
 *
 * This used to cap at three and hide the rest behind "+ 2 more", which the
 * social team had to click open one day at a time — on a view whose entire
 * purpose is seeing the month at a glance. Angel's job here is cadence, and a
 * day that reads "3 posts" when it holds seven is not showing cadence, it is
 * hiding it.
 *
 * The cells were never the constraint: .calendar-day is a min-height and the
 * grid rows are auto, so they have always been able to grow. The cap was in
 * this file. It is gone, and the guard against a pathological day is now a
 * max-height with an internal scroll on the pill list (components.css), which
 * only engages far past any realistic posting load.
 */

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Local ISO date. Never toISOString on a local date — that shifts the day. */
function isoOf(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The cells of a month view: whole weeks, Monday first, including the days
 * either side that fill the first and last rows. Those neighbours are shown
 * greyed rather than blank so the grid reads as a calendar rather than a
 * ragged table.
 */
export function monthGrid(year: number, monthIndex: number): { iso: string; inMonth: boolean }[] {
  const first = new Date(year, monthIndex, 1);
  // getDay is Sunday-first; shift so Monday starts the week.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, monthIndex, 1 - lead);

  const cells: { iso: string; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({ iso: isoOf(day), inMonth: day.getMonth() === monthIndex });
    // Stop after a whole week once the month is behind us: a six-row grid for
    // a month that fits in five is just empty space.
    if (i % 7 === 6 && day.getMonth() !== monthIndex && day > first) {
      break;
    }
  }
  return cells;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** What a pill shows beyond its title. All off on the main Calendar. */
interface PillOptions {
  hidePlatformDots: boolean;
  showFormat: boolean;
  showTime: boolean;
}

function PostPill({
  post,
  onOpen,
  draggable,
  options,
}: {
  post: SocialPost;
  onOpen: (id: string) => void;
  draggable: boolean;
  options: PillOptions;
}) {
  const platforms = options.hidePlatformDots ? [] : platformStyles(post.platforms);
  const time = options.showTime ? pillTime(post.publish_time) : null;
  const format = options.showFormat ? post.type.trim() : '';
  return (
    <button
      type="button"
      className="calendar-pill"
      data-status={post.status}
      draggable={draggable}
      onDragStart={(event) => {
        event.dataTransfer.setData('text/plain', post.id);
        event.dataTransfer.effectAllowed = 'move';
      }}
      onClick={() => onOpen(post.id)}
      title={`${post.title} — ${SOCIAL_STATUS_LABELS[post.status]}${
        platforms.length === 0 ? '' : ` · ${platforms.map((p) => p.label).join(', ')}`
      }${time === null ? '' : ` · ${time}`}${format === '' ? '' : ` · ${format}`}`}
    >
      {/* A status stripe rather than a badge: colour carries the state, and the
          space a badge would take is the space the title needs. */}
      <span className={`calendar-pill-stripe hub-status--${SOCIAL_STATUS_TONES[post.status]}`} />
      <span className="calendar-pill-title">{post.title}</span>
      {time === null ? null : <span className="calendar-pill-time">{time}</span>}
      {format === '' ? null : <span className="calendar-pill-format">{format}</span>}
      {/* Where the main Calendar puts its platform dots: the end of the pill.
          Shown even with no Type, as the neutral dot, so a missing format is
          visible rather than silent. */}
      {options.showFormat ? <FormatDot kind={formatKind(format)} detail={format} /> : null}
      {platforms.length === 0 ? null : (
        <span className="calendar-pill-dots">
          {platforms.map((platform) => (
            <span
              key={platform.key}
              className="platform-dot"
              // The custom property, not `background` — the rule paints from
              // it, and the ring that makes a pale dot visible comes with it.
              style={{ '--platform-color': platform.color } as CSSProperties}
              aria-label={platform.label}
              title={platform.label}
            />
          ))}
        </span>
      )}
    </button>
  );
}

export function CalendarView({
  posts,
  onOpenPost,
  onAddOnDate,
  onMovePost,
  canDrag = false,
  hidePlatformDots = false,
  showFormat = false,
  showTime = false,
  ariaLabel = 'Publishing calendar',
  initialYear,
  initialMonth,
}: {
  posts: SocialPost[];
  onOpenPost: (id: string) => void;
  /** Clicking an empty part of a day starts a post already dated to it. */
  onAddOnDate: (iso: string) => void;
  /**
   * Dropping a pill on another day reschedules the SAME post to it. The page
   * supplies the write (one update to publish_date, never a copy), so this
   * view stays renderable without providers — which is also what its tests
   * depend on.
   */
  onMovePost?: (postId: string, iso: string) => void;
  /**
   * Whether pills offer a drag at all. Mirrors the Board's rule: offering a
   * drag the database will refuse is a card that springs back with an error
   * nobody can act on.
   */
  canDrag?: boolean;
  /**
   * The LinkedIn Calendar's pill: no platform dots (every one would be the
   * same LinkedIn blue), the format (the Type field) as a small tag plus a
   * format dot, and the publish time. All default off, so the main Calendar
   * is unchanged.
   */
  hidePlatformDots?: boolean;
  showFormat?: boolean;
  showTime?: boolean;
  /** The grid's accessible name, so two calendars on one page differ. */
  ariaLabel?: string;
  /**
   * Which month to open on. Today's, unless told otherwise — which is only
   * ever in a test, where "today" would make the assertions expire: a fixture
   * dated August 2026 stops being on screen in September.
   */
  initialYear?: number;
  initialMonth?: number;
}) {
  const today = new Date();
  const [cursor, setCursor] = useState({
    year: initialYear ?? today.getFullYear(),
    month: initialMonth ?? today.getMonth(),
  });
  /** The day a drag is currently held over, for the drop-target highlight. */
  const [dragOver, setDragOver] = useState<string | null>(null);

  const byDate = useMemo(() => {
    const grouped = new Map<string, SocialPost[]>();
    for (const post of posts) {
      if (post.publish_date === null) {
        continue;
      }
      const bucket = grouped.get(post.publish_date) ?? [];
      bucket.push(post);
      grouped.set(post.publish_date, bucket);
    }
    return grouped;
  }, [posts]);

  const cells = monthGrid(cursor.year, cursor.month);
  const todayIso = isoOf(today);

  function shift(by: number) {
    const next = new Date(cursor.year, cursor.month + by, 1);
    setCursor({ year: next.getFullYear(), month: next.getMonth() });
  }

  return (
    // A real element rather than a fragment, so the shell can widen ITS OWN
    // container for this view with :has() — the same escape the notes app and
    // the project split pane already use. A fragment gives the stylesheet
    // nothing to match on, and the alternative (teaching the shell about
    // routes) puts page layout knowledge in the wrong file.
    <div className="social-calendar">
      <div className="calendar-bar">
        <button type="button" className="status-chip" onClick={() => shift(-1)}>
          ← Previous
        </button>
        <h3 className="project-category-heading">
          {MONTH_NAMES[cursor.month]} {cursor.year}
        </h3>
        <button type="button" className="status-chip" onClick={() => shift(1)}>
          Next →
        </button>
        <button
          type="button"
          className="status-chip"
          onClick={() => setCursor({ year: today.getFullYear(), month: today.getMonth() })}
        >
          Today
        </button>
      </div>

      <div className="calendar-grid" role="grid" aria-label={ariaLabel}>
        {WEEKDAYS.map((day) => (
          <div key={day} className="calendar-weekday" role="columnheader">
            {day}
          </div>
        ))}

        {cells.map((cell) => {
          const dayPosts = byDate.get(cell.iso) ?? [];
          return (
            <div
              key={cell.iso}
              role="gridcell"
              className={`calendar-day${cell.inMonth ? '' : ' calendar-day--outside'}${
                cell.iso === todayIso ? ' calendar-day--today' : ''
              }${dragOver === cell.iso ? ' calendar-day--over' : ''}`}
              /* A SINGLE click on the cell would fire whenever somebody misses
                 a pill, which is why only the day number takes one. A double
                 click is deliberate — the Google Calendar gesture — and it is
                 ignored when it lands on a pill or the day number, because a
                 double-click on a control is about that control. */
              onDoubleClick={(event) => {
                if (event.target instanceof Element && event.target.closest('button') !== null) {
                  return;
                }
                onAddOnDate(cell.iso);
              }}
              onDragOver={
                onMovePost === undefined
                  ? undefined
                  : (event) => {
                      event.preventDefault();
                      setDragOver(cell.iso);
                    }
              }
              onDragLeave={onMovePost === undefined ? undefined : () => setDragOver(null)}
              onDrop={
                onMovePost === undefined
                  ? undefined
                  : (event) => {
                      event.preventDefault();
                      setDragOver(null);
                      const id = event.dataTransfer.getData('text/plain');
                      if (id === '') {
                        return;
                      }
                      // Dropping a post back on its own day is not a move, and
                      // writing it anyway would put a no-op in the audit log.
                      const dragged = posts.find((post) => post.id === id);
                      if (dragged === undefined || dragged.publish_date === cell.iso) {
                        return;
                      }
                      onMovePost(id, cell.iso);
                    }
              }
            >
              {/* The day number adds a post on that date. Clicking the cell
                  itself would fire whenever somebody misses a pill. */}
              <button
                type="button"
                className="calendar-day-number"
                aria-label={`Add a post on ${cell.iso}`}
                onClick={() => onAddOnDate(cell.iso)}
              >
                {Number(cell.iso.slice(8, 10))}
              </button>
              <ul className="calendar-pills">
                {dayPosts.map((post) => (
                  <li key={post.id}>
                    <PostPill
                      post={post}
                      onOpen={onOpenPost}
                      draggable={canDrag}
                      options={{ hidePlatformDots, showFormat, showTime }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
